import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, ExternalLink, Unplug, Loader2, CreditCard,
  User, Mail, Phone, KeyRound, Building2, MapPin, Receipt, Save, X, Info, Tags,
} from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import CatalogModal from "../../components/app/CatalogModal";

export default function Settings() {
  const { activeWorkspace, user, refreshMe } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [billing, setBilling] = useState(null);
  const [members, setMembers] = useState([]);
  const [wsDetail, setWsDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const load = async () => {
    if (!activeWorkspace) return;
    const [s, m, b, d] = await Promise.all([
      api.xeroStatus(),
      api.members(activeWorkspace.id).catch(() => []),
      activeWorkspace.id ? api.billingStatus().catch(() => null) : Promise.resolve(null),
      activeWorkspace.id ? api.workspaceDetail(activeWorkspace.id).catch(() => null) : Promise.resolve(null),
    ]);
    setStatus(s);
    setMembers(m);
    setBilling(b);
    setWsDetail(d);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspace?.id]);

  const myRole = members.find((m) => m.user_id === user?.id)?.role;
  const isOwner = myRole === "owner" || activeWorkspace?.is_platform_access;
  const canManageCatalogs = isOwner || myRole === "admin";

  const connect = async () => {
    setBusy(true);
    try {
      const { auth_url } = await api.xeroConnect();
      window.location.href = auth_url;
    } catch (ex) {
      toast({ title: "Couldn't start Xero connection", description: apiErrorMessage(ex, "") });
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Xero from this workspace? You can reconnect anytime.")) return;
    await api.xeroDisconnect();
    await load();
    toast({ title: "Disconnected from Xero" });
  };

  const trialEndFmt = billing?.trial_end ? new Date(billing.trial_end).toLocaleDateString() : null;
  const periodEndFmt = billing?.current_period_end ? new Date(billing.current_period_end).toLocaleDateString() : null;
  const planLabel = billing?.plan === "starter" ? "Starter (Free)"
    : billing?.plan ? `${billing.plan[0].toUpperCase() + billing.plan.slice(1)} · ${billing.plan_period || "monthly"}` : "—";

  return (
    <div>
      <div className="text-sm text-[#6a736f]">Settings</div>
      <h1 className="font-display text-4xl mt-1">Workspace</h1>

      {/* --- Your account -------------------------------------------------- */}
      <h2 className="font-display text-2xl mt-8">Your account</h2>
      <ProfileCard user={user} onSaved={refreshMe} toast={toast} />
      <EmailCard user={user} onSaved={refreshMe} toast={toast} />
      <PasswordCard user={user} toast={toast} />

      {/* --- Business details (workspace, owner-only) ---------------------- */}
      {isOwner && (
        <>
          <h2 className="font-display text-2xl mt-10">Business details</h2>
          <BusinessCard
            workspaceId={activeWorkspace?.id}
            detail={wsDetail}
            onSaved={(d) => setWsDetail(d)}
            toast={toast}
          />
        </>
      )}

      {/* --- Catalogs ----------------------------------------------------- */}
      {canManageCatalogs && (
        <>
          <h2 className="font-display text-2xl mt-10">Catalogs</h2>
          <div className="mt-4 bg-white rounded-xl border border-cream p-6" data-testid="catalogs-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-cream-2 flex items-center justify-center text-forest">
                  <Tags size={20} />
                </div>
                <div>
                  <div className="font-semibold text-lg">Expense categories</div>
                  <p className="text-sm text-[#3f4a44] max-w-md mt-1">
                    Manage the list of categories used when logging project expenses.
                    New workspaces are seeded with defaults like Travel, Meals, Software.
                  </p>
                </div>
              </div>
              <button onClick={() => setCatalogOpen(true)}
                      data-testid="manage-expense-categories-btn"
                      className="btn-outline-forest inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium">
                <Tags size={14} /> Manage categories
              </button>
            </div>
          </div>
        </>
      )}

      {/* --- Billing ------------------------------------------------------ */}
      <h2 className="font-display text-2xl mt-10">Billing</h2>
      <div className="mt-4 bg-white rounded-xl border border-cream p-6" data-testid="billing-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-forest text-white flex items-center justify-center">
              <CreditCard size={20} />
            </div>
            <div>
              <div className="font-semibold text-lg">Subscription</div>
              <p className="text-sm text-[#3f4a44] max-w-md mt-1">
                Manage your Full Bench subscription, seats, invoices, and payment method.
              </p>
              <div className="mt-3 text-sm">
                <div><span className="text-[#6a736f]">Plan:</span> <span className="font-medium" data-testid="billing-plan">{planLabel}</span></div>
                {billing?.subscription_status && (
                  <div><span className="text-[#6a736f]">Status:</span> <span className="font-medium" data-testid="billing-status">{billing.subscription_status}</span></div>
                )}
                {billing?.seats > 1 && <div><span className="text-[#6a736f]">Seats:</span> {billing.seats}</div>}
                {billing?.subscription_status === "trialing" && trialEndFmt && (
                  <div className="text-coral">Trial ends {trialEndFmt}.</div>
                )}
                {periodEndFmt && billing.subscription_status !== "trialing" && (
                  <div>Next invoice: {periodEndFmt}</div>
                )}
                {billing?.cancel_at_period_end && (
                  <div className="text-coral">Cancels at period end.</div>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {billing?.has_stripe_customer ? (
              isOwner ? (
                <Link to="/app/account" data-testid="billing-manage-btn" className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium">
                  Manage billing
                </Link>
              ) : (
                <div className="text-xs text-[#6a736f] max-w-[200px] text-right">Ask your workspace owner to manage billing.</div>
              )
            ) : (
              isOwner && (
                <Link to="/app/account" data-testid="billing-upgrade-btn" className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium">
                  Choose a plan
                </Link>
              )
            )}
          </div>
        </div>
      </div>

      <h2 className="font-display text-2xl mt-10">Integrations</h2>

      <div className="mt-6 bg-white rounded-xl border border-cream p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-[#13B5EA] flex items-center justify-center text-white font-bold text-xl">X</div>
            <div>
              <div className="font-semibold text-lg">Xero</div>
              <p className="text-sm text-[#3f4a44] max-w-md mt-1">Push Full Bench invoices to Xero as Draft ACCREC invoices. Clients are auto-created as Contacts. You review and authorise in Xero.</p>
              {!status ? (
                <div className="mt-2 text-xs text-[#6a736f] inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading…</div>
              ) : !status.configured ? (
                <div className="mt-2 text-xs text-[#B92E14]">Xero is not configured on the server yet.</div>
              ) : status.connected ? (
                <div className="mt-2 text-xs inline-flex items-center gap-1 text-[#2E7157]">
                  <CheckCircle2 size={14} /> Connected to <span className="font-medium">{status.tenant_name}</span>
                </div>
              ) : (
                <div className="mt-2 text-xs text-[#6a736f]">Not connected.</div>
              )}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {status?.connected ? (
              isOwner && (
                <button onClick={disconnect} className="btn-outline-forest inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium">
                  <Unplug size={14} /> Disconnect
                </button>
              )
            ) : status?.configured && (
              isOwner ? (
                <button disabled={busy} onClick={connect} className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium">
                  <ExternalLink size={14} /> {busy ? "Redirecting…" : "Connect Xero"}
                </button>
              ) : (
                <div className="text-xs text-[#6a736f] max-w-[200px] text-right">Ask your workspace owner to connect Xero.</div>
              )
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 text-xs text-[#6a736f]">More integrations coming soon (MYOB, QuickBooks, Slack).</div>

      {catalogOpen && (
        <CatalogModal
          title="Expense categories"
          hint="Categories used when logging project expenses. Archive removes them from selectors but keeps existing expenses intact."
          listFn={api.expenseCategories}
          createFn={api.createExpenseCategory}
          updateFn={api.updateExpenseCategory}
          deleteFn={api.deleteExpenseCategory}
          testid="expense-categories-catalog"
          onClose={() => setCatalogOpen(false)}
        />
      )}

      <DangerZone email={user?.email} />
    </div>
  );
}

// ---------- Account cards ----------

function SectionCard({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <div className="mt-4 bg-white rounded-xl border border-cream p-6" data-testid={testid}>
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-lg bg-cream text-forest flex items-center justify-center shrink-0">
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{title}</div>
          {subtitle && <p className="text-sm text-[#3f4a44] mt-1">{subtitle}</p>}
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

function ProfileCard({ user, onSaved, toast }) {
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [target, setTarget] = useState(user?.weekly_target_hours ?? 40);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setName(user?.name || "");
    setPhone(user?.phone || "");
    setTarget(user?.weekly_target_hours ?? 40);
  }, [user?.id]);

  const dirty = name !== (user?.name || "")
    || phone !== (user?.phone || "")
    || Number(target) !== Number(user?.weekly_target_hours ?? 40);

  const save = async () => {
    setBusy(true);
    try {
      await api.patchMe({ name, phone, weekly_target_hours: Number(target) });
      await onSaved?.();
      toast({ title: "Profile updated" });
    } catch (ex) {
      toast({ title: "Couldn't save profile", description: apiErrorMessage(ex, "") });
    } finally { setBusy(false); }
  };

  return (
    <SectionCard icon={User} title="Profile" subtitle="Your name, contact phone, and weekly hours target." testid="profile-card">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-[#3f4a44]">Full name</label>
          <input
            value={name} onChange={(e) => setName(e.target.value)}
            data-testid="profile-name-input"
            className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#3f4a44]">Contact phone</label>
          <div className="relative">
            <Phone size={14} className="absolute left-3 top-3 text-[#6a736f]" />
            <input
              value={phone} onChange={(e) => setPhone(e.target.value)}
              data-testid="profile-phone-input" placeholder="+61 400 000 000"
              className="mt-1 w-full border border-cream rounded-md pl-8 pr-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#3f4a44]">Weekly hours target</label>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="number" min={0} max={168} step="0.5"
              value={target} onChange={(e) => setTarget(e.target.value)}
              data-testid="profile-weekly-target-input"
              className="w-28 border border-cream rounded-md px-3 py-2 text-sm"
            />
            <span className="text-xs text-[#6a736f]">hours / week — drives the progress chip on your Timesheet</span>
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          onClick={save} disabled={!dirty || busy}
          data-testid="save-profile-btn"
          className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Save size={14} /> {busy ? "Saving…" : "Save profile"}
        </button>
      </div>
    </SectionCard>
  );
}

function EmailCard({ user, onSaved, toast }) {
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [link, setLink] = useState("");

  const cancelPending = async () => {
    setBusy(true);
    try { await api.cancelEmailChange(); await onSaved?.(); toast({ title: "Email change cancelled" }); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setMsg(""); setLink(""); setBusy(true);
    try {
      const r = await api.changeEmail({ new_email: newEmail.trim(), password });
      await onSaved?.();
      if (r.sent) {
        setMsg(`Confirmation link sent to ${r.pending_email}. Click it to complete the switch.`);
      } else {
        setLink(r.link);
        setMsg(`Email delivery isn't set up yet — open the link below to confirm the switch.`);
      }
      setPassword("");
    } catch (ex) {
      setMsg(apiErrorMessage(ex, "Couldn't start email change."));
    } finally { setBusy(false); }
  };

  return (
    <SectionCard icon={Mail} title="Login email" subtitle="Changing your email requires confirmation from the new address." testid="email-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm">
            <b>{user?.email}</b>
            {user?.email_verified ? (
              <span className="ml-2 text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5">verified</span>
            ) : (
              <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">not verified</span>
            )}
          </div>
          {user?.pending_email && (
            <div className="mt-1 text-xs text-coral flex items-center gap-2" data-testid="pending-email-badge">
              <Info size={12} />
              Pending change to <b>{user.pending_email}</b> — awaiting confirmation.
              <button onClick={cancelPending} disabled={busy} className="underline">Cancel</button>
            </div>
          )}
        </div>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            data-testid="open-change-email"
            className="btn-outline-forest px-4 py-2 rounded-md text-sm font-medium"
          >
            Change email
          </button>
        )}
      </div>

      {open && (
        <form onSubmit={submit} className="mt-4 border-t border-cream pt-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-[#3f4a44]">New email</label>
            <input
              type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              data-testid="new-email-input" autoFocus
              className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#3f4a44]">Current password</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="change-email-password"
              className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm"
            />
          </div>
          {msg && (
            <div className="text-xs text-[#3f4a44]" data-testid="change-email-msg">
              {msg}
              {link && <div className="mt-1 break-all"><a href={link} className="text-coral underline">{link}</a></div>}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button
              type="button" onClick={() => { setOpen(false); setMsg(""); setLink(""); setPassword(""); }}
              className="btn-outline-forest inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm"
            >
              <X size={14} /> Close
            </button>
            <button
              type="submit" disabled={busy}
              data-testid="submit-change-email"
              className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40"
            >
              {busy ? "Sending…" : "Send confirmation link"}
            </button>
          </div>
        </form>
      )}
    </SectionCard>
  );
}

function PasswordCard({ user, toast }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [link, setLink] = useState("");

  const send = async () => {
    setMsg(""); setLink(""); setBusy(true);
    try {
      const r = await api.requestPasswordReset(user.email);
      if (r.sent) setMsg(`We sent a reset link to ${user.email}. It expires in 2 hours.`);
      else if (r.link) { setLink(r.link); setMsg("Email delivery isn't set up yet — open the link below to reset."); }
      else if (r.reason === "sso_only") setMsg("This account signs in via SSO — there's no password to reset.");
      else setMsg("Please try again in a moment.");
      toast({ title: "Reset link sent", description: user.email });
    } catch (ex) {
      setMsg(apiErrorMessage(ex, "Couldn't send reset email."));
    } finally { setBusy(false); }
  };

  return (
    <SectionCard icon={KeyRound} title="Password" subtitle="Send yourself a one-time link to choose a new password." testid="password-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-[#3f4a44]">
          We'll email a reset link to <b>{user?.email}</b>.
        </div>
        <button
          onClick={send} disabled={busy}
          data-testid="send-password-reset"
          className="btn-outline-forest inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40"
        >
          {busy ? "Sending…" : "Email me a reset link"}
        </button>
      </div>
      {msg && (
        <div className="mt-3 text-xs text-[#3f4a44]" data-testid="password-reset-msg">
          {msg}
          {link && <div className="mt-1 break-all"><a href={link} className="text-coral underline">{link}</a></div>}
        </div>
      )}
    </SectionCard>
  );
}

function BusinessCard({ workspaceId, detail, onSaved, toast }) {
  const [form, setForm] = useState({
    name: "", office_address: "", billing_address: "",
    tax_label: "VAT", tax_number: "", contact_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [copyOffice, setCopyOffice] = useState(false);

  useEffect(() => {
    if (!detail) return;
    setForm({
      name: detail.name || "",
      office_address: detail.office_address || "",
      billing_address: detail.billing_address || "",
      tax_label: detail.tax_label || "VAT",
      tax_number: detail.tax_number || "",
      contact_phone: detail.contact_phone || "",
    });
  }, [detail?.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!workspaceId) return;
    setBusy(true);
    try {
      const payload = { ...form };
      if (copyOffice) payload.billing_address = form.office_address;
      const updated = await api.patchWorkspace(workspaceId, payload);
      onSaved?.(updated);
      toast({ title: "Business details saved" });
    } catch (ex) {
      toast({ title: "Couldn't save business details", description: apiErrorMessage(ex, "") });
    } finally { setBusy(false); }
  };

  return (
    <SectionCard
      icon={Building2}
      title="Company & tax"
      subtitle="Used on invoices you send from Full Bench. Only workspace owners can edit."
      testid="business-card"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#3f4a44]">Business name</label>
          <input
            value={form.name} onChange={set("name")}
            data-testid="business-name-input"
            className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#3f4a44]">Company phone</label>
          <input
            value={form.contact_phone} onChange={set("contact_phone")}
            data-testid="business-phone-input" placeholder="+61 400 000 000"
            className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#3f4a44] flex items-center gap-1">
            <Receipt size={12} /> Tax label
          </label>
          <select
            value={form.tax_label} onChange={set("tax_label")}
            data-testid="tax-label-select"
            className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="VAT">VAT</option>
            <option value="GST">GST</option>
            <option value="ABN">ABN</option>
            <option value="EIN">EIN</option>
            <option value="TAX ID">Tax ID</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#3f4a44]">{form.tax_label || "Tax"} number</label>
          <input
            value={form.tax_number} onChange={set("tax_number")}
            data-testid="tax-number-input"
            placeholder="e.g. GB123456789"
            className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#3f4a44] flex items-center gap-1">
            <MapPin size={12} /> Office address
          </label>
          <textarea
            value={form.office_address} onChange={set("office_address")}
            data-testid="office-address-input" rows={3}
            className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm resize-none"
          />
        </div>
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-[#3f4a44] flex items-center gap-1">
              <MapPin size={12} /> Billing address
            </label>
            <label className="text-xs text-[#6a736f] inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox" checked={copyOffice} onChange={(e) => setCopyOffice(e.target.checked)}
                data-testid="copy-office-to-billing"
              />
              Same as office
            </label>
          </div>
          <textarea
            value={copyOffice ? form.office_address : form.billing_address}
            onChange={set("billing_address")}
            disabled={copyOffice}
            data-testid="billing-address-input" rows={3}
            className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm resize-none disabled:bg-cream-2"
          />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          onClick={save} disabled={busy}
          data-testid="save-business-btn"
          className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40"
        >
          <Save size={14} /> {busy ? "Saving…" : "Save business details"}
        </button>
      </div>
    </SectionCard>
  );
}

// ---------- Danger zone (unchanged) ----------

function DangerZone({ email }) {
  const [open, setOpen] = React.useState(false);
  const [confirm, setConfirm] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api.deleteAccount();
      localStorage.removeItem("fb_token");
      localStorage.removeItem("fb_workspace_id");
      window.location.href = "/";
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      if (typeof d === "string") setErr(d);
      else if (d?.message) {
        const list = (d.workspaces || []).map((w) => `“${w.name}” (${w.other_members} other member${w.other_members === 1 ? "" : "s"})`).join(", ");
        setErr(`${d.message}${list ? ` Workspaces: ${list}.` : ""}`);
      } else setErr("Couldn't delete account. Try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-12 border-t border-red-200 pt-8" data-testid="danger-zone">
      <h2 className="font-display text-2xl text-red-700">Danger zone</h2>
      <div className="mt-4 bg-white rounded-xl border border-red-200 p-6 flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="font-semibold text-forest">Delete my account</div>
          <p className="text-sm text-[#3f4a44] mt-1 max-w-md">
            Cancels any active subscription and permanently deletes solo workspaces you own along with
            their projects, invoices and time entries. Workspaces with other active members must be
            transferred first.
          </p>
        </div>
        {!open ? (
          <button
            onClick={() => setOpen(true)}
            data-testid="open-delete-account"
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Delete account
          </button>
        ) : (
          <div className="w-full max-w-md">
            <label className="text-xs font-semibold text-red-700">
              Type your email <span className="font-mono">{email}</span> to confirm
            </label>
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="delete-confirm-input"
              className="mt-1 w-full border border-red-300 rounded-md px-3 py-2 text-sm"
            />
            {err && <div className="mt-2 text-xs text-red-700" data-testid="delete-error">{err}</div>}
            <div className="mt-3 flex gap-2">
              <button
                disabled={busy || confirm.trim().toLowerCase() !== (email || "").toLowerCase()}
                onClick={submit}
                data-testid="confirm-delete-account"
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? "Deleting…" : "Yes, delete forever"}
              </button>
              <button
                onClick={() => { setOpen(false); setConfirm(""); setErr(""); }}
                className="btn-outline-forest px-4 py-2 rounded-md text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
